// Renders one inspector control per style kind, straight from the schema.
// A new style key needs no code here as long as its kind already exists.

const el = (tag, className, props = {}) => Object.assign(document.createElement(tag), { className, ...props })

const LABEL_CLASS = "mb-1 block text-[11px] font-medium uppercase tracking-wide text-slate-500"
const INPUT_CLASS =
  "w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-xs " +
  "dark:border-slate-700 dark:bg-slate-900"
const SEGMENT_CLASS = "flex flex-wrap gap-1"

const segmentButton = (label, active) => {
  const button = el("button", "", { type: "button" })
  button.textContent = label
  button.className =
    "rounded border px-1.5 py-1 text-[11px] leading-none transition " +
    (active
      ? "border-cyan-500 bg-cyan-500 text-white"
      : "border-slate-300 text-slate-600 hover:border-cyan-400 dark:border-slate-700 dark:text-slate-300")
  return button
}

// Token presets as a segmented control. Clicking the active token clears it,
// which is how a user removes a value they set by mistake.
const tokenControl = (definition, current, labels, onChange) => {
  const wrap = el("div", SEGMENT_CLASS)
  ;(definition.values || []).forEach((token) => {
    const button = segmentButton(labels.tokens[token] || token, current === token)
    button.addEventListener("click", () => onChange(current === token ? null : token))
    wrap.append(button)
  })
  return wrap
}

const swatchControl = (definition, current, onChange) => {
  const wrap = el("div", SEGMENT_CLASS)
  ;(definition.values || []).forEach((token) => {
    const button = el("button", "", { type: "button", title: token })
    button.style.background = `var(--${token})`
    button.className =
      "h-6 w-6 rounded-full border-2 transition " +
      (current === token ? "border-cyan-500 scale-110" : "border-slate-300 dark:border-slate-600")
    button.addEventListener("click", () => onChange(current === token ? null : token))
    wrap.append(button)
  })
  return wrap
}

const numberControl = (current, onChange, { suffix = "px", min = 0, max = 400 } = {}) => {
  const wrap = el("div", "flex items-center gap-2")
  const input = el("input", INPUT_CLASS, { type: "number", min, max })
  input.value = parseFloat(current) || ""
  const slider = el("input", "flex-1", { type: "range", min, max })
  slider.value = parseFloat(current) || 0

  const push = (value) => onChange(value === "" ? null : `${value}${suffix}`)
  input.addEventListener("change", () => { slider.value = input.value; push(input.value) })
  slider.addEventListener("input", () => { input.value = slider.value; push(slider.value) })

  wrap.append(input, slider)
  return wrap
}

const colorControl = (current, onChange) => {
  const wrap = el("div", "flex items-center gap-2")
  const picker = el("input", "h-7 w-10 rounded border border-slate-300", { type: "color" })
  picker.value = /^#[0-9a-f]{6}$/i.test(current || "") ? current : "#000000"
  const hex = el("input", INPUT_CLASS, { type: "text", placeholder: "#000000" })
  hex.value = current || ""

  picker.addEventListener("input", () => { hex.value = picker.value; onChange(picker.value) })
  hex.addEventListener("change", () => onChange(hex.value || null))

  wrap.append(picker, hex)
  return wrap
}

const isToken = (value, tokens) => value !== null && tokens.includes(value)

// Opens the media library and resolves to an asset id. The control never
// deals in URLs, which is what keeps "replace this image everywhere" possible.
const assetControl = (value, labels, onChange) => {
  const wrap = el("div", "flex items-center gap-2")

  const choose = el("button", "flex-1 rounded-md border border-slate-300 px-2 py-1 text-xs " +
                              "hover:border-cyan-400 dark:border-slate-700", { type: "button" })
  choose.textContent = value ? labels.ui.replace_image || labels.ui.choose : labels.ui.choose
  choose.addEventListener("click", () => {
    choose.dispatchEvent(new CustomEvent("builder:pick-asset", {
      bubbles: true,
      detail: { onPick: (asset) => onChange(asset.id) }
    }))
  })
  wrap.append(choose)

  if (value) {
    const clear = el("button", "text-[11px] text-slate-400 hover:text-red-600", { type: "button" })
    clear.textContent = "×"
    clear.addEventListener("click", () => onChange(null))
    wrap.append(clear)
  }

  return wrap
}

// One labelled row: preset control by default, precise input when toggled or
// when the stored value is already raw.
export const styleControl = ({ key, definition, value, labels, tokens, onChange }) => {
  const row = el("div", "mb-3")
  const header = el("div", "mb-1 flex items-center justify-between gap-2")
  const label = el("span", LABEL_CLASS)
  label.textContent = labels.keys[key] || key
  header.append(label)

  const kind = definition.kind
  const hybrid = ["length", "font_size", "radius", "color"].includes(kind)
  const raw = value !== null && !isToken(value, tokens.all)

  if (hybrid) {
    const toggle = el("button", "text-[10px] text-slate-400 hover:text-cyan-600", { type: "button" })
    toggle.textContent = raw ? labels.ui.revert_to_theme : labels.ui.precise
    toggle.addEventListener("click", () => {
      if (raw) return onChange(null)
      row.dataset.precise = row.dataset.precise === "true" ? "false" : "true"
      row.dispatchEvent(new CustomEvent("control:refresh", { bubbles: true }))
    })
    header.append(toggle)
  }

  row.append(header)

  const precise = raw || row.dataset.precise === "true"
  let control

  if (hybrid && precise) {
    control = kind === "color" ? colorControl(value, onChange) : numberControl(value, onChange)
  } else if (kind === "color") {
    control = swatchControl(definition, value, onChange)
  } else if (kind === "enum" || kind === "token") {
    control = tokenControl(definition, value, labels, onChange)
  } else if (kind === "number") {
    control = numberControl(value, (v) => onChange(v === null ? null : String(parseInt(v, 10))), { suffix: "", min: 100, max: 900 })
  } else if (kind === "percent") {
    control = numberControl(value, onChange, { suffix: "%", min: 0, max: 100 })
  } else if (kind === "asset") {
    control = assetControl(value, labels, onChange)
  } else {
    control = tokenControl(definition, value, labels, onChange)
  }

  row.append(control)
  return row
}

export const propControl = ({ key, rule, value, labels, onChange }) => {
  const row = el("div", "mb-3")
  const label = el("label", LABEL_CLASS)
  label.textContent = key
  row.append(label)

  if (rule.kind === "asset") {
    row.append(assetControl(value, labels, onChange))
    return row
  }

  let input
  if (rule.kind === "enum") {
    input = el("select", INPUT_CLASS)
    ;(rule.values || []).forEach((option) => {
      const node = el("option", "", { value: option })
      node.textContent = option
      node.selected = String(value) === option
      input.append(node)
    })
  } else if (rule.kind === "integer") {
    input = el("input", INPUT_CLASS, { type: "number", min: rule.min, max: rule.max })
    input.value = value ?? ""
  } else {
    input = el("input", INPUT_CLASS, { type: "text" })
    input.value = value ?? ""
  }

  input.addEventListener("change", () => onChange(input.value))
  row.append(input)
  return row
}
